/* eslint-disable react/react-in-jsx-scope */
import axios from "axios"
import { motion } from "framer-motion"
import { useEffect, useState } from "react"
import { Card, CardBody, Col, Row, Spinner } from "reactstrap"
import "./CardProcesso.css"
import formatNumber from "./formatNumber"

const classificationStats = [
  {
    title: "Total de Processos - Total",
    key: "total",
    icon: "bx bx-folder",
    gradient: "linear-gradient(135deg, #6B8DE3 0%, #4318FF 100%)",
    section: "totalProcessos"
  },
  {
    title: "Total de Processos - Ontem",
    key: "ontem",
    icon: "bx bx-calendar-check",
    gradient: "linear-gradient(135deg, #49B5C0 0%, #1A97F5 100%)",
    section: "totalProcessos"
  },
  {
    title: "Total de Processos - No Mês",
    key: "noMes",
    icon: "bx bx-calendar",
    gradient: "linear-gradient(135deg, #FFAA2B 0%, #FB9678 100%)",
    section: "totalProcessos"
  },
  {
    title: "Classificados como Lead - Total",
    key: "total",
    icon: "bx bx-check-circle",
    gradient: "linear-gradient(135deg, #53B947 0%, #169F85 100%)",
    section: "classificadosComoLead"
  },
  {
    title: "Classificados como Lead - Ontem",
    key: "ontem",
    icon: "bx bx-calendar-check",
    gradient: "linear-gradient(135deg, #49B5C0 0%, #1A97F5 100%)",
    section: "classificadosComoLead"
  },
  {
    title: "Classificados como Lead - No Mês",
    key: "noMes",
    icon: "bx bx-calendar",
    gradient: "linear-gradient(135deg, #FFAA2B 0%, #FB9678 100%)",
    section: "classificadosComoLead"
  },
  {
    title: "Classificados como Não Lead - Total",
    key: "total",
    icon: "bx bx-x-circle",
    gradient: "linear-gradient(135deg, #FD7E14 0%, #E74C3C 100%)",
    section: "classificadosComoNaoLead"
  },
  {
    title: "Classificados como Não Lead - Ontem",
    key: "ontem",
    icon: "bx bx-calendar-check",
    gradient: "linear-gradient(135deg, #DC3545 0%, #AD1741 100%)",
    section: "classificadosComoNaoLead"
  },
  {
    title: "Classificados como Não Lead - No Mês",
    key: "noMes",
    icon: "bx bx-calendar",
    gradient: "linear-gradient(135deg, #DC3545 0%, #AD1741 100%)",
    section: "classificadosComoNaoLead"
  },
  {
    title: "Não Priorizados - Total",
    key: "total",
    icon: "bx bx-minus-circle",
    gradient: "linear-gradient(135deg, #6C757D 0%, #495057 100%)",
    section: "naoPriorizados"
  },
  {
    title: "Não Priorizados - Ontem",
    key: "ontem",
    icon: "bx bx-calendar-check",
    gradient: "linear-gradient(135deg, #DC3545 0%, #AD1741 100%)",
    section: "naoPriorizados"
  },
  {
    title: "Não Priorizados - No Mês",
    key: "noMes",
    icon: "bx bx-calendar",
    gradient: "linear-gradient(135deg, #DC3545 0%, #AD1741 100%)",
    section: "naoPriorizados"
  },
]

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
}

const cardVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: "spring", stiffness: 100, damping: 12 },
  },
}

const numberVariants = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: { type: "spring", stiffness: 100 },
  },
}

const CardClassificacao = () => {
  const [classificationData, setClassificationData] = useState({
    totalProcessos: {
      total: 0,
      ontem: 0,
      noMes: 0
    },
    classificadosComoLead: {
      total: 0,
      ontem: 0,
      noMes: 0
    },
    classificadosComoNaoLead: {
      total: 0,
      ontem: 0,
      noMes: 0
    },
    naoPriorizados: {
      total: 0,
      ontem: 0,
      noMes: 0
    }
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchClassificationStats = async () => {
      try {
        setLoading(true)
        const response = await axios.get(`${process.env.REACT_APP_API_EXT_LEADS}/api/processos/classification-overview`)
        
        setClassificationData(response.data)
        setError(null)
      } catch (err) {
        console.error('Erro ao buscar estatísticas de classificação:', err)
        setError(err.response?.data?.message || err.message || 'Erro ao carregar dados')
      } finally {
        setLoading(false)
      }
    }

    fetchClassificationStats()
  }, [])

  const getClassificationValue = (stat) => {
    if (stat.section) {
      return classificationData[stat.section]?.[stat.key] || 0
    }
    return classificationData[stat.key] || 0
  }

  const renderClassificationValue = stat =>
    loading ? (
      <Spinner size="sm" color="light" />
    ) : error ? (
      <span className="text-white-50">Erro</span>
    ) : (
      formatNumber(getClassificationValue(stat))
    )

  return (
    <div className="dashboard-stats">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <Card className="shadow-lg border-0 rounded-lg">
          <CardBody>
            <Row className="mb-4">
              <Col lg="12">
                <h3 className="text-gradient fw-bold">
                  Estatísticas de Classificação
                </h3>
              </Col>
            </Row>
            <Row>
              {classificationStats.map((stat, index) => (
                <Col lg="3" md="6" key={index}>
                  <motion.div variants={cardVariants}>
                    <Card
                      className="stat-card border-0 h-100"
                      style={{ background: stat.gradient }}
                    >
                      <CardBody className="p-4">
                        <div className="d-flex align-items-center mb-3">
                          <div className="stat-icon me-3">
                            <span className="rounded-circle bg-white bg-opacity-10 text-white">
                              <i className={`${stat.icon} fs-4`} />
                            </span>
                          </div>
                          <h5 className="text-white mb-0 fw-semibold">
                            {stat.title}
                          </h5>
                        </div>
                        <div className="mt-4">
                          <motion.div
                            variants={numberVariants}
                            className="d-flex align-items-center"
                          >
                            <h2 className="text-white mb-0 me-2 fw-bold">
                              {renderClassificationValue(stat)}
                            </h2>
                            <span className="badge bg-white bg-opacity-10 text-white">
                              Atual
                            </span>
                          </motion.div>
                        </div>
                      </CardBody>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </CardBody>
        </Card>
      </motion.div>
    </div>
  )
}

export default CardClassificacao